import { useCallback, useEffect, useRef } from 'react';
import { captureMessage } from '@sentry/react';
import { useChannels } from '@/context/channels';
import { addRecommendationBreadcrumb } from '@/context/channels/recommendation/addRecommendationBreadcrumb';
import type { WebSocketError } from '@/context/channels/types';
import { logWebSocketError } from '@/context/channels/utils';
import {
  RecommendationProcessingStatus,
  useCourseRecommendations,
} from '@/data/recommendation/course-recommendations';
import { useRecommendationProfile } from '@/data/recommendation/profiles';

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 3000;

export const useRecommendationChannel = () => {
  const { profile } = useRecommendationProfile();
  const { data, refetch } = useCourseRecommendations({
    profileId: profile?.id,
    enabled: false,
    // Não queremos que as recomendações fiquem fazendo fetch automaticamente aqui, só precisamos do refetch
  });
  const { subscribe, unsubscribe, disconnect } = useChannels();
  const recommendation = data?.['home/main'];
  const previousRecommendationId = recommendation?.id;
  const cleanupRef = useRef<() => void>(() => {});

  useEffect(() => {
    return () => {
      cleanupRef.current();
    };
  }, []);

  const handleReceived = useCallback(async () => {
    disconnect();
    await refetch();
  }, [disconnect, refetch]);

  const subscribeRecommendation = useCallback(async () => {
    cleanupRef.current();

    let cancelled = false;
    cleanupRef.current = () => {
      cancelled = true;
      unsubscribe();
    };

    addRecommendationBreadcrumb(
      'Starting CourseRecommendation subscription flow',
      {
        recommendation,
        previousRecommendationId,
      }
    );

    const result = await refetch();

    const freshRecommendation = result.data?.['home/main'];
    const id = freshRecommendation?.id;

    addRecommendationBreadcrumb(
      'Fetched CourseRecommendation data after refetch',
      {
        recommendation: freshRecommendation,
        previousRecommendationId,
      }
    );

    if (id == null) {
      addRecommendationBreadcrumb(
        'No recommendation ID found, aborting subscription'
      );
      return;
    }

    const isRecommendationFinished =
      freshRecommendation?.processing_status ===
        RecommendationProcessingStatus.Completed ||
      freshRecommendation?.processing_status ===
        RecommendationProcessingStatus.Failed;

    if (isRecommendationFinished) {
      addRecommendationBreadcrumb(
        'Recommendation already finished, skipping subscription',
        {
          recommendation: freshRecommendation,
          previousRecommendationId,
        }
      );
      return;
    }

    addRecommendationBreadcrumb(
      'Subscribing to CourseRecommendation WebSocket',
      {
        recommendation: freshRecommendation,
        previousRecommendationId,
      }
    );

    const subscriptionData = {
      channel: 'ProcessingStatusChannel',
      resource_type: 'Recommendation::CourseRecommendation',
      resource_id: id,
    };

    const subscribeWithRetry = (attempt = 0) => {
      subscribe(subscriptionData, {
        received: handleReceived,
        onError: (error: WebSocketError) => {
          if (cancelled) return;

          if (attempt >= MAX_RETRIES) {
            captureMessage(
              `WebSocket Error: max retries reached for [${subscriptionData.resource_type} | ${subscriptionData.resource_id}]`,
              {
                level: 'error',
                tags: {
                  component: 'websocket',
                  resource_type: subscriptionData.resource_type,
                },
              }
            );
            return;
          }

          if (attempt > 0) logWebSocketError(subscriptionData, error);
          setTimeout(() => subscribeWithRetry(attempt + 1), RETRY_DELAY_MS);
        },
      });
    };

    subscribeWithRetry();
  }, [
    refetch,
    previousRecommendationId,
    recommendation,
    subscribe,
    handleReceived,
    unsubscribe,
  ]);

  return { subscribeRecommendation };
};
